import type { RouteRecordRaw } from 'vue-router'

import { ROUTES } from '~/scopes/auth/route-names'

import { CHECKOUT_E_COMMERCE } from './route-names'

/**
 * Bouygues authentication routes with full paths
 * These should be imported directly in checkout/routes.ts without nesting
 */
export const bouyguesAuthRoutes: RouteRecordRaw[] = [
  // Desktop auth route with BouyguesWebRoot wrapper
  {
    path: 'f2/services/bouygues-telecom',
    component: () =>
      import('~/scopes/checkout/flows/e-commerce/pages/BouyguesWebRoot/BouyguesWebRoot.vue'),
    meta: {
      owners: ['bot-squad-partnerships-front'],
      auth: {
        mode: ['authenticated'],
        redirection: {
          default: {
            name: ROUTES.AUTH.LOGIN,
            query: { bm_journey: 'checkout' },
          },
        },
      },
      tracking: { contentGroup: 'Services Checkout' },
    },
    children: [
      {
        path: 'auth',
        name: CHECKOUT_E_COMMERCE.SERVICES.BOUYGUES.AUTH,
        component: () =>
          import('~/scopes/checkout/flows/e-commerce/pages/BouyguesAuthentication/BouyguesAuthentication.vue'),
        meta: {
          layout: 'minimal',
          owners: ['bot-squad-partnerships-front'],
        },
      },
    ],
  },
  // Webview auth route with BouyguesWebviewRoot wrapper
  {
    path: 'f2/webview/services/bouygues-telecom',
    component: () =>
      import('~/scopes/checkout/flows/e-commerce/pages/BouyguesWebviewRoot/BouyguesWebviewRoot.vue'),
    meta: {
      owners: ['bot-squad-partnerships-front'],
      auth: {
        mode: ['authenticated'],
        redirection: {
          default: {
            name: ROUTES.AUTH.LOGIN,
            query: { bm_journey: 'checkout' },
          },
        },
      },
      tracking: { contentGroup: 'Services Checkout' },
    },
    children: [
      {
        path: 'auth',
        name: CHECKOUT_E_COMMERCE.WEBVIEW.SERVICES.BOUYGUES.AUTH,
        component: () =>
          import('~/scopes/checkout/flows/e-commerce/pages/BouyguesAuthentication/BouyguesAuthentication.vue'),
        meta: {
          layout: 'minimal',
          owners: ['bot-squad-partnerships-front'],
        },
      },
    ],
  },
]

/**
 * Bouygues desktop flow routes (nested under CheckoutFlow with header)
 * These should be imported in e-commerce routes with relative paths
 */
export const bouyguesFlowRoutes: RouteRecordRaw[] = [
  {
    path: 'services/bouygues-telecom',
    name: CHECKOUT_E_COMMERCE.SERVICES.BOUYGUES.ROOT,
    redirect: { name: CHECKOUT_E_COMMERCE.SERVICES.BOUYGUES.SIGN_UP },
    component: () =>
      import('~/scopes/checkout/flows/e-commerce/pages/BouyguesWebRoot/BouyguesWebRoot.vue'),
    meta: {
      owners: ['bot-squad-partnerships-front'],
      auth: {
        mode: ['authenticated'],
        redirection: {
          default: {
            name: ROUTES.AUTH.LOGIN,
            query: { bm_journey: 'checkout' },
          },
        },
      },
      tracking: { contentGroup: 'Services Checkout' },
    },
    children: [
      {
        path: 'sign-up',
        name: CHECKOUT_E_COMMERCE.SERVICES.BOUYGUES.SIGN_UP,
        component: () =>
          import('~/scopes/checkout/flows/e-commerce/pages/BouyguesSignUp/BouyguesSignUp.vue'),
        meta: {
          owners: ['bot-squad-partnerships-front'],
        },
      },
      {
        path: 'phoneline-setup',
        name: CHECKOUT_E_COMMERCE.SERVICES.BOUYGUES.PHONELINE_SETUP,
        component: () =>
          import('~/scopes/checkout/flows/e-commerce/pages/BouyguesPhonelineSetup/BouyguesPhonelineSetup.vue'),
        meta: {
          owners: ['bot-squad-partnerships-front'],
        },
      },
      {
        path: 'not-eligible',
        name: CHECKOUT_E_COMMERCE.SERVICES.BOUYGUES.NOT_ELIGIBLE,
        component: () =>
          import('~/scopes/checkout/flows/e-commerce/pages/BouyguesPhonelineSetup/components/NotEligible/NotEligible.vue'),
        meta: {
          owners: ['bot-squad-partnerships-front'],
        },
      },
    ],
  },
]

/**
 * Bouygues webview flow routes (nested under separate CheckoutFlow without header)
 * These should be imported at top level in e-commerce routes with full paths
 */
export const bouyguesWebviewFlowRoutes: RouteRecordRaw[] = [
  {
    path: 'f2/webview/services/bouygues-telecom',
    component: () =>
      import('@backmarket/nuxt-layer-checkout/components/CheckoutFlow/CheckoutFlow.vue'),
    redirect: { name: CHECKOUT_E_COMMERCE.WEBVIEW.SERVICES.BOUYGUES.SIGN_UP },
    meta: {
      owners: ['bot-squad-partnerships-front'],
      layout: false,
      tracking: { contentGroup: 'Services Checkout' },
    },
    children: [
      {
        path: '',
        name: CHECKOUT_E_COMMERCE.WEBVIEW.SERVICES.BOUYGUES.ROOT,
        component: () =>
          import('~/scopes/checkout/flows/e-commerce/pages/BouyguesWebviewRoot/BouyguesWebviewRoot.vue'),
        meta: {
          auth: {
            mode: ['authenticated'],
            redirection: {
              default: {
                name: ROUTES.AUTH.LOGIN,
                query: { bm_journey: 'checkout' },
              },
            },
          },
        },
        children: [
          {
            path: 'sign-up',
            name: CHECKOUT_E_COMMERCE.WEBVIEW.SERVICES.BOUYGUES.SIGN_UP,
            component: () =>
              import('~/scopes/checkout/flows/e-commerce/pages/BouyguesSignUp/BouyguesSignUp.vue'),
            meta: {
              owners: ['bot-squad-partnerships-front'],
            },
          },
          {
            path: 'phoneline-setup',
            name: CHECKOUT_E_COMMERCE.WEBVIEW.SERVICES.BOUYGUES.PHONELINE_SETUP,
            component: () =>
              import('~/scopes/checkout/flows/e-commerce/pages/BouyguesPhonelineSetup/BouyguesPhonelineSetup.vue'),
            meta: {
              owners: ['bot-squad-partnerships-front'],
            },
          },
          {
            path: 'not-eligible',
            name: CHECKOUT_E_COMMERCE.WEBVIEW.SERVICES.BOUYGUES.NOT_ELIGIBLE,
            component: () =>
              import('~/scopes/checkout/flows/e-commerce/pages/BouyguesPhonelineSetup/components/NotEligible/NotEligible.vue'),
            meta: {
              owners: ['bot-squad-partnerships-front'],
            },
          },
        ],
      },
    ],
  },
]
